import { useCallback, useEffect, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { client } from '../lib/api'
import { Logo, OpenTestBanner, OUTREACH_LABEL, OUTREACH_OPTIONS, type OutreachPreference } from '../lib/ui'
import { TurnstileWidget } from './TurnstileWidget'
import { isNativeApp, nativePlatform, startNativeGoogleAuth } from '../lib/nativeAuth'
import { captureAffiliateRefFromLocation, clearStoredAffiliateRef, getStoredAffiliateRef } from '../lib/affiliate'

const OAUTH_ERRORS: Record<string, string> = {
  google_not_configured: 'Google sign-in is not available yet.',
  account_suspended: 'This account is suspended.',
  google_email_required: 'Google did not share an email address — try another account.',
  access_denied: 'Google sign-in was cancelled.',
  invalid_state: 'Sign-in expired — try again.',
  missing_code: 'Google sign-in failed — try again.',
}

function oauthErrorMessage(code: string | null): string | null {
  if (!code) return null
  return OAUTH_ERRORS[code] ?? decodeURIComponent(code)
}

export function AuthPage({ mode, onDone }: { mode: 'signin' | 'signup'; onDone: (dest?: string) => void }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [ageConfirmed, setAgeConfirmed] = useState(false)
  const [outreach, setOutreach] = useState<OutreachPreference>('either')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(null)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [googleEnabled, setGoogleEnabled] = useState(false)
  const [affiliateCode, setAffiliateCode] = useState(() => captureAffiliateRefFromLocation() ?? '')

  useEffect(() => {
    const hash = window.location.hash
    const q = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : ''
    const params = new URLSearchParams(q)
    const oauthErr = oauthErrorMessage(params.get('error'))
    if (oauthErr) setErr(oauthErr)
    const ref = captureAffiliateRefFromLocation()
    if (ref) setAffiliateCode(ref)
  }, [])

  useEffect(() => {
    client
      .publicConfig()
      .then((cfg) => {
        setTurnstileSiteKey(cfg.turnstileSiteKey)
        setGoogleEnabled(!!cfg.googleOAuthEnabled)
      })
      .catch(() => undefined)
  }, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErr(null)
    if (turnstileSiteKey && !turnstileToken) {
      setErr('Complete the security check below, then try again.')
      return
    }
    setBusy(true)
    try {
      if (mode === 'signup') {
        await client.register({
          email,
          password,
          displayName,
          ageConfirmed,
          outreachPreference: outreach,
          turnstileToken: turnstileToken ?? undefined,
          affiliateCode: (affiliateCode || getStoredAffiliateRef() || undefined)?.trim() || undefined,
        })
        clearStoredAffiliateRef()
      } else {
        await client.login({ email, password, turnstileToken: turnstileToken ?? undefined })
      }
      onDone()
    } catch (error) {
      setErr((error as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const googleSignIn = async () => {
    setErr(null)
    if (mode === 'signup' && !ageConfirmed) {
      setErr('Confirm you are 18+ before continuing with Google.')
      return
    }
    const platform = nativePlatform()
    const url = client.googleAuthUrl(mode, {
      ageConfirmed: mode === 'signup' ? ageConfirmed : undefined,
      returnTo: '/onboarding',
      platform: platform ?? undefined,
      affiliateCode: mode === 'signup' ? affiliateCode || getStoredAffiliateRef() || undefined : undefined,
    })

    if (isNativeApp()) {
      setBusy(true)
      try {
        const result = await startNativeGoogleAuth(url)
        if (result.error) {
          setErr(oauthErrorMessage(result.error) ?? result.error)
          return
        }
        if (mode === 'signup') clearStoredAffiliateRef()
        onDone(result.dest)
      } finally {
        setBusy(false)
      }
      return
    }

    if (mode === 'signup') clearStoredAffiliateRef()
    window.location.href = url
  }

  const onTurnstileToken = useCallback((token: string | null) => setTurnstileToken(token), [])

  return (
    <div className={`auth-page${isNativeApp() ? ' auth-page--native' : ''}`}>
      <div className="auth-card">
        {!isNativeApp() && (
          <a className="back-link" href="#/">
            <ArrowLeft size={15} /> Back
          </a>
        )}
        <Logo />
        <OpenTestBanner compact />
        <h1>{mode === 'signup' ? 'Join PairVelle' : 'Welcome back'}</h1>
        <p className="page-sub">
          {mode === 'signup'
            ? 'Same rules for everyone. Interest has to mean something.'
            : isNativeApp()
              ? 'Sign in to continue in the app.'
              : 'Sign in to continue.'}
        </p>

        {googleEnabled && (
          <>
            <button type="button" className="button google-auth" disabled={busy} onClick={googleSignIn}>
              <span className="google-auth-icon" aria-hidden="true">
                G
              </span>
              Continue with Google
            </button>
            <p className="auth-divider">
              <span>or</span>
            </p>
          </>
        )}

        <form className="auth-form" onSubmit={submit}>
          {mode === 'signup' && (
            <div className="field">
              <label htmlFor="auth-name">Display name</label>
              <input
                id="auth-name"
                className="input"
                required
                maxLength={40}
                autoComplete="nickname"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>
          )}
          <div className="field">
            <label htmlFor="auth-email">Email</label>
            <input
              id="auth-email"
              className="input"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="auth-pass">Password</label>
            <input
              id="auth-pass"
              className="input"
              type="password"
              required
              minLength={10}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {mode === 'signup' && <span className="field-hint">At least 10 characters.</span>}
            {mode === 'signin' && (
              <a className="text-button" href="#/forgot-password" style={{ marginTop: 8, display: 'inline-block' }}>
                Forgot password?
              </a>
            )}
          </div>

          {mode === 'signup' && (
            <>
              <div className="field">
                <label>Outreach preference</label>
                <div className="segmented">
                  {OUTREACH_OPTIONS.map((opt) => (
                    <button
                      type="button"
                      key={opt.value}
                      className={outreach === opt.value ? 'selected' : ''}
                      onClick={() => setOutreach(opt.value)}
                    >
                      {OUTREACH_LABEL[opt.value]}
                    </button>
                  ))}
                </div>
                <span className="field-hint">{OUTREACH_OPTIONS.find((o) => o.value === outreach)?.blurb}</span>
              </div>
              <div className="field">
                <label htmlFor="auth-ref">Invite code (optional)</label>
                <input
                  id="auth-ref"
                  className="input"
                  value={affiliateCode}
                  onChange={(e) => setAffiliateCode(e.target.value.trim().toLowerCase())}
                  placeholder="If someone invited you"
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={16}
                />
                <span className="field-hint">Earns you a credit bonus when valid.</span>
              </div>
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  required
                  checked={ageConfirmed}
                  onChange={(e) => setAgeConfirmed(e.target.checked)}
                />
                I confirm I am 18 or older
              </label>
            </>
          )}

          {turnstileSiteKey && (
            <TurnstileWidget siteKey={turnstileSiteKey} onToken={onTurnstileToken} onError={setErr} />
          )}

          {err && (
            <div className="form-error" role="alert">
              {err}
            </div>
          )}

          <button className="button primary" type="submit" disabled={busy} style={{ width: '100%' }}>
            {busy ? 'Please wait…' : mode === 'signup' ? 'Create account' : 'Sign in'}
          </button>
        </form>

        <p className="auth-switch">
          {mode === 'signup' ? (
            <>
              Already a member? <button onClick={() => (window.location.hash = '/signin')}>Sign in</button>
            </>
          ) : (
            <>
              New here? <button onClick={() => (window.location.hash = '/signup')}>Create an account</button>
            </>
          )}
        </p>
      </div>
    </div>
  )
}
